import {
  ContentChannel,
  ContentKind,
  THREAD_MAX_POSTS,
  charLimitViolation,
  supportsThreads,
} from '@mg/shared';
import type {
  SocialPlatform,
  ThreadConversion,
  ThreadConversionChange,
  ThreadConversionPost,
} from '@mg/shared';
import type { EditorBlock } from './blocks';
import { canStartThread, isThreadForm, threadPosts } from './thread';

/**
 * "Convert to thread with AI" in the composer, decided here: when the suggestion card is offered,
 * how the AI's posts go into the form, how Undo takes them out again, whether Undo or Regenerate
 * would throw away something the person did, and which posts may show their changes.
 *
 * Everything here is a pure function of what it is given. None of them makes a key or a block:
 * the posts' blocks are made in the click handler (useThreadConversion.ts) and handed in, because
 * a state updater runs twice under StrictMode - as `startThread` is handed its second post. What
 * moves between these - the request in flight, the original text, the review - is the hook's.
 */

/** What the suggestion card is about: the single post's text box, and how far over its limit. */
export interface ConversionOffer {
  /** The key of the post's one TEXT block. */
  textKey: string;
  /** Its text exactly as the box holds it - what a conversion sends, and what Undo puts back. */
  text: string;
  /** The text as the platform counts it ({@link charLimitViolation}), and the limit it is over. */
  count: number;
  limit: number;
}

/**
 * The single post the card offers to convert, or null when no card can show. Asked on every render,
 * so a platform switch, an edit or a paste shows, hides or renumbers the card with no state of its
 * own (spec sections 1 and 7).
 *
 * Only a SOCIAL post (never Blog, Video or Podcast) going to X or Threads (`platform` is the
 * destination the counters judge by); never a post that can no longer change (`readOnly`); only the
 * single-post layout - a thread is never offered one (spec section 5); and only a post that could
 * start a thread by hand ({@link canStartThread}: one text box, at most one media panel, no PDF),
 * or its save as a thread would be refused. And only while its text is over the limit by the rule
 * the red counter and the Save gate use: the same block, untrimmed, counted the platform's way. So
 * the card, the red count and the blocked Save always agree.
 */
export function conversionOffer(a: {
  channel: ContentChannel;
  platform: SocialPlatform | null;
  readOnly: boolean;
  blocks: readonly EditorBlock[];
}): ConversionOffer | null {
  const { channel, platform, readOnly, blocks } = a;
  if (channel !== ContentChannel.SOCIAL || !supportsThreads(platform) || readOnly) return null;
  if (isThreadForm(blocks) || canStartThread(blocks) != null) return null;
  const box = blocks.find((b) => b.kind === ContentKind.TEXT);
  const over = box ? charLimitViolation(platform, box.text) : null;
  return box && over
    ? { textKey: box.key, text: box.text, count: over.count, limit: over.limit }
    : null;
}

/**
 * Whether an answer from the server can go into the form as a thread: two posts or more (one post
 * is no thread), no more than a thread can hold, each with text and its list of changes - parts the
 * view can draw, or none. The server promises all of it; an answer that breaks it is a failure,
 * never a half-applied thread, and never a "Show changes" that breaks the composer as it draws.
 */
export function usableConversion(answer: unknown): answer is ThreadConversion {
  const posts = (answer as { posts?: unknown } | null | undefined)?.posts;
  return (
    Array.isArray(posts) &&
    posts.length >= 2 &&
    posts.length <= THREAD_MAX_POSTS &&
    posts.every(isUsablePost)
  );
}

function isUsablePost(post: unknown): post is ThreadConversionPost {
  const p = post as Partial<ThreadConversionPost> | null | undefined;
  return (
    typeof p?.text === 'string' &&
    p.text.trim() !== '' &&
    Array.isArray(p.changes) &&
    p.changes.every(isChange)
  );
}

const CHANGE_KINDS: readonly unknown[] = ['same', 'added', 'removed'];

/** One part of a post's changes: kept, added or removed, and its text. */
function isChange(change: unknown): change is ThreadConversionChange {
  const c = change as Partial<ThreadConversionChange> | null | undefined;
  return typeof c?.text === 'string' && CHANGE_KINDS.includes(c.kind);
}

/**
 * How a conversion's posts go into the form: made in the click handler, keys and all, and applied
 * by {@link applyConversion} inside the state updater.
 */
export interface ConversionPlan {
  /** The single post's TEXT block, by key, and the text it held when the conversion was asked. */
  textKey: string;
  original: string;
  /** Post 1's `threadKey`: every block of the single post goes under it. */
  firstKey: string;
  /** Post 1's text, which replaces the TEXT block's. */
  firstText: string;
  /** Posts 2..N: one TEXT block each, each already carrying its own `threadKey`. */
  rest: EditorBlock[];
}

/**
 * Put a conversion into the form. Every block of the single post becomes post 1 under `firstKey` -
 * keys, ids and order untouched, only the TEXT block's text replaced by post 1's - and posts 2..N
 * follow. This is `startThread`'s rule, so the media the post had stays on post 1 exactly as it
 * was (spec section 4), an upload still on its way lands in post 1, and saving a post that exists
 * updates its text row rather than deleting it and writing a new one.
 *
 * Checked HERE, on the list the change is applied to, because the answer was asked for from an
 * earlier one: the list comes back as it is unless it is still a single post that could start a
 * thread, and its TEXT block `textKey` still holds exactly `original` - the text the posts were
 * made from. A plan with no second post is no thread, and changes nothing either.
 */
export function applyConversion(prev: EditorBlock[], plan: ConversionPlan): EditorBlock[] {
  if (plan.rest.length === 0 || isThreadForm(prev) || canStartThread(prev) != null) return prev;
  const box = prev.find((b) => b.key === plan.textKey);
  if (!box || box.kind !== ContentKind.TEXT || box.text !== plan.original) return prev;
  return [
    ...prev.map((b) =>
      b === box
        ? { ...b, text: plan.firstText, threadKey: plan.firstKey }
        : { ...b, threadKey: plan.firstKey },
    ),
    ...plan.rest,
  ];
}

/** One of the AI's posts, as the review remembers it: its text box, and what the AI wrote there. */
export interface ReviewPost {
  /** The key of the post's TEXT block. Post 1's is the single post's own text box. */
  key: string;
  /** The text the AI gave it. */
  text: string;
  /** Its "Show changes", against the original text (ThreadConversionPost.changes). */
  changes: ThreadConversionChange[];
}

/** What the review of a conversion keeps until it ends: enough to put the single post back. */
export interface ConversionReview {
  /** The single post's TEXT block - post 1's text box since the conversion. */
  textKey: string;
  /** A copy of that block as it was when the posts went in: Undo's way back when post 1 is gone. */
  textBlock: EditorBlock;
  /** The text that was converted, exactly as it was sent. */
  original: string;
  /** The AI's posts, in the order they went in. */
  posts: readonly ReviewPost[];
}

/**
 * Undo a conversion: the form is the single post again, holding the original text (spec section
 * 4).
 *
 * The post that holds the text box `textKey` - wherever it sits now - is that single post: its
 * text box gets `original` back, and its media stay as they are NOW (a picture added to post 1
 * since is kept, one removed stays removed). Every other post goes, and no block keeps a
 * `threadKey`. If that post was deleted, the text box comes back alone with the original text -
 * the same key and id, so saving a post that exists still updates its text row.
 */
export function undoConversion(
  prev: EditorBlock[],
  review: Pick<ConversionReview, 'textKey' | 'textBlock' | 'original'>,
): EditorBlock[] {
  const home = threadPosts(prev).find((p) => p.blocks.some((b) => b.key === review.textKey));
  return (home ? home.blocks : [review.textBlock]).map((b) => {
    const next = b.key === review.textKey ? { ...b, text: review.original } : { ...b };
    delete next.threadKey;
    return next;
  });
}

/**
 * Whether Undo or Regenerate would throw away something the person did since the conversion, and
 * so has to ask first (spec section 4). Anything either would lose counts: a post whose text is not
 * the AI's any more (a hand edit, or the AI assist writing into it); a post added, deleted or
 * moved - the text boxes, in order, are not the AI's posts by key; or a file on any post but post
 * 1.
 *
 * Post 1's media do not count, added or removed: Undo and Regenerate keep them as they are
 * ({@link undoConversion}), so nothing of them is lost. Neither does an empty media tile anywhere -
 * there is nothing in it to lose.
 */
export function conversionEdited(
  blocks: readonly EditorBlock[],
  review: Pick<ConversionReview, 'textKey' | 'posts'>,
): boolean {
  const posts = threadPosts(blocks);
  if (posts.length !== review.posts.length) return true;
  const rewritten = posts.some((p, i) => {
    const box = p.blocks.find((b) => b.kind === ContentKind.TEXT);
    return !box || box.key !== review.posts[i].key || box.text !== review.posts[i].text;
  });
  if (rewritten) return true;
  return posts.some(
    (p) =>
      !p.blocks.some((b) => b.key === review.textKey) &&
      p.blocks.some((b) => b.kind !== ContentKind.TEXT && b.assetId != null),
  );
}

/**
 * Whether the form still holds the thread a conversion made: it is a thread, and one of the AI's
 * posts at least is in it. The review - the banner, Show changes, Undo and Regenerate - lasts
 * exactly that long. A thread cut back to one post is a single post again, and a form the posts
 * never went into (its text changed while the request was away) was never converted.
 */
export function conversionInForm(
  blocks: readonly EditorBlock[],
  review: Pick<ConversionReview, 'posts'>,
): boolean {
  if (!isThreadForm(blocks)) return false;
  const keys = new Set(review.posts.map((p) => p.key));
  return blocks.some((b) => keys.has(b.key));
}

/**
 * "Show changes": for each of the AI's posts, by the key of its text box, the changes to draw -
 * while the box holds exactly the AI's text and the changes spell it - else null, and the post is
 * drawn as its plain text, read-only. So a post edited by hand loses its highlights whatever the
 * switch does, and has them again once it is edited back to the AI's text, because they match again
 * (spec section 4). Changes the server could not line up (none) are never drawn as highlights.
 */
export function reviewChanges(
  blocks: readonly EditorBlock[],
  review: Pick<ConversionReview, 'posts'>,
): ReadonlyMap<string, ThreadConversionChange[] | null> {
  const textOf = new Map(
    blocks.filter((b) => b.kind === ContentKind.TEXT).map((b) => [b.key, b.text] as const),
  );
  return new Map(
    review.posts.map((p) => [
      p.key,
      textOf.get(p.key) === p.text && spells(p.changes, p.text) ? p.changes : null,
    ]),
  );
}

/** The kept and the added parts of `changes`, in order, are `text` exactly, as the server says. */
function spells(changes: readonly ThreadConversionChange[], text: string): boolean {
  return (
    changes
      .filter((c) => c.kind !== 'removed')
      .map((c) => c.text)
      .join('') === text
  );
}
